import React, { useRef, useEffect, memo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import Transition from '../utils/Transition';
import { useUser } from '../userContext';
import { useCampaignSearch } from '../hooks/useCampaignSearch';
import { MagnifyingGlassIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';

const SearchModal = memo(({ id, searchId, modalOpen, setModalOpen }) => {
  const { campaigns, businessProfile } = useUser();
  const { searchTerm, setSearchTerm, results: searchResults, isLoading } = useCampaignSearch(businessProfile?.id);

  const modalContent = useRef(null);
  const searchInput = useRef(null);

  // Helper to get display name (internal name or title)
  const getDisplayName = (campaign) => campaign.internalName || campaign.options?.title || 'Untitled Game';
  
  // Helper to get subtitle if internal name differs from title
  const getSubtitle = (campaign) => {
    if (campaign.internalName && campaign.options?.title && campaign.internalName !== campaign.options?.title) {
      return campaign.options.title;
    }
    return null;
  };

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!modalOpen || !modalContent.current || modalContent.current.contains(target)) return
      setModalOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!modalOpen || keyCode !== 27) return;
      setModalOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  useEffect(() => {
    modalOpen && searchInput.current.focus();
  }, [modalOpen]);

  const modalElement = (
    <>
      {/* Modal backdrop */}
      <Transition
        className="fixed inset-0 bg-black/70 z-[9998] transition-opacity"
        style={{
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)'
        }}
        show={modalOpen}
        enter="transition ease-out duration-200"
        enterStart="opacity-0"
        enterEnd="opacity-100"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        aria-hidden="true"
      />
      {/* Modal dialog */}
      <Transition
        id={id}
        className="fixed inset-0 z-[9999] flex justify-center px-4 sm:px-6 pt-20"
        style={{
          alignItems: 'flex-start'
        }}
        role="dialog"
        aria-modal="true"
        show={modalOpen}
        enter="transition ease-in-out duration-200"
        enterStart="opacity-0 translate-y-4"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-in-out duration-200"
        leaveStart="opacity-100 translate-y-0"
        leaveEnd="opacity-0 translate-y-4"
      >
        <div
          ref={modalContent}
          className="overflow-auto max-w-2xl w-full rounded-2xl gch-bg-card border border-[var(--admin-border)] shadow-2xl"
          style={{
            maxHeight: 'calc(100vh - 20rem)'
          }}
        >
          {/* Search form */}
          <form className="p-3 sm:p-4" onSubmit={(e) => e.preventDefault()}>
            <div className="relative">
              <label htmlFor={searchId} className="sr-only">Search Games</label>
              <input
                id={searchId}
                className="w-full appearance-none rounded-xl text-sm transition-all duration-200 py-3 pl-11 pr-4 gch-input-ghost border border-[var(--admin-border)] gch-text-primary placeholder:gch-text-muted hover:border-[var(--admin-border)] hover:bg-white/5 focus:gch-input-ghost focus:border-[var(--admin-accent)] focus:ring-2 focus:ring-[var(--admin-accent)]/15 focus:outline-none"
                style={{fontFamily: '"Inter", sans-serif'}}
                type="search"
                placeholder="Search games..."
                ref={searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button className="absolute inset-0 right-auto group flex items-center" type="submit" aria-label="Search">
                <MagnifyingGlassIcon className="ml-4 mr-2 h-5 w-5 gch-text-muted group-hover:gch-text-primary transition-colors" />
              </button>
            </div>
          </form>

          <div className="py-2 px-2 border-t border-[var(--admin-border)]">
            {/* Search Results */}
            {searchTerm && (
              <div className="mb-3 last:mb-0">
                <div className="text-xs font-semibold uppercase tracking-wide px-2 mb-2 text-admin-label">
                  Search Results
                </div>
                <ul className="text-sm">
                  {isLoading ? (
                    <li className="px-3 py-2 font-medium gch-text-secondary">Searching…</li>
                  ) : searchResults.length > 0 ? (
                    searchResults.map(campaign => (
                      <li key={campaign.id}>
                        <Link
                          className="flex items-center p-3 rounded-xl transition-colors duration-200 font-medium gch-text-primary hover:bg-white/5 border border-transparent hover:border-[var(--admin-accent)]/40"
                          to={`/studio/campaign/${campaign.id}`}
                          onClick={() => setModalOpen(false)}
                        >
                          <div className="flex flex-col">
                            <span className="font-semibold gch-text-primary">{getDisplayName(campaign)}</span>
                            {getSubtitle(campaign) && (
                              <span className="text-xs gch-text-secondary">{getSubtitle(campaign)}</span>
                            )}
                          </div>
                        </Link>
                      </li>
                    ))
                  ) : (
                    <li className="px-3 py-2 font-medium gch-text-secondary">
                      No games found. Search matches internal name or title (from the start).
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* Recent Campaigns */}
            {!searchTerm && campaigns && campaigns.length > 0 && (
              <div className="mb-3 last:mb-0">
                <div className="text-xs font-semibold uppercase tracking-wide px-2 mb-2 text-admin-label">
                  Recent Games
                </div>
                <ul className="text-sm">
                  {campaigns
                    .sort((a, b) => {
                      const aTime = a.createdAt?.seconds || a.createdAt?.toDate?.()?.getTime() || 0;
                      const bTime = b.createdAt?.seconds || b.createdAt?.toDate?.()?.getTime() || 0;
                      return bTime - aTime;
                    })
                    .slice(0, 3)
                    .map(campaign => (
                      <li key={campaign.id}>
                        <Link
                          className="flex items-center p-3 rounded-xl transition-colors duration-200 font-medium gch-text-primary hover:bg-white/5 border border-transparent hover:border-[var(--admin-accent)]/40"
                          to={`/studio/campaign/${campaign.id}`}
                          onClick={() => setModalOpen(false)}
                        >
                          <div className="flex flex-col">
                            <span className="font-semibold gch-text-primary">{getDisplayName(campaign)}</span>
                            {getSubtitle(campaign) && (
                              <span className="text-xs gch-text-secondary">{getSubtitle(campaign)}</span>
                            )}
                          </div>
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {/* Empty state */}
            {!searchTerm && (!campaigns || campaigns.length === 0) && (
              <div className="text-center py-8">
                <RocketLaunchIcon className="h-12 w-12 mx-auto mb-3 text-admin-icon" />
                <div className="font-semibold mb-2 gch-text-primary text-lg" style={{fontFamily: '"Lexend", sans-serif'}}>
                  No Games Yet
                </div>
                <div className="text-sm gch-text-secondary font-medium">
                  Create your first game to get started
                </div>
              </div>
            )}
          </div>
        </div>
      </Transition>
    </>
  );

  return createPortal(modalElement, document.body);
});

export default SearchModal;
